/**
 * Product Datasheet v2 JavaScript
 * Handles: dropdown interaction on product pages, project info from URL params,
 * timestamp generation, and print/PDF functionality.
 *
 * Product data is rendered server-side via Liquid in product.datasheet-v2.liquid
 */

(function() {
  'use strict';

  window.theme = window.theme || {};

  theme.datasheet = {
    initialized: false,

    init: function() {
      if (this.initialized) return;
      this.initialized = true;

      var datasheetPage = document.querySelector('[data-datasheet-page]');
      if (datasheetPage) {
        this.handleProjectInfo();
        this.generateTimestamp();
        this.setPdfTitle();
        window.addEventListener('beforeprint', function() {
          theme.datasheet.setPdfTitle();
        });
        this.setupManualPrint();
        return;
      }

      // Not on the datasheet page — initialize dropdown on product page
      this.initDropdown();
    },

    /* ========================================================================
       DROPDOWN (Product Page)
       ======================================================================== */

    initDropdown: function() {
      var containers = document.querySelectorAll('[data-datasheet-container]');
      if (!containers.length) return;

      for (var i = 0; i < containers.length; i++) {
        this.initSingleDropdown(containers[i]);
      }

      // Global click listener to close all open dropdowns when clicking outside
      document.addEventListener('click', function(e) {
        var allContainers = document.querySelectorAll('[data-datasheet-container]');
        for (var j = 0; j < allContainers.length; j++) {
          if (!allContainers[j].contains(e.target)) {
            var t = allContainers[j].querySelector('[data-datasheet-trigger]');
            var d = allContainers[j].querySelector('[data-datasheet-dropdown]');
            var cv = d ? d.querySelector('[data-choice-view]') : null;
            var fv = d ? d.querySelector('[data-form-view]') : null;
            if (t && d) theme.datasheet.closeDropdown(t, d, cv, fv);
          }
        }
      });
    },

    initSingleDropdown: function(container) {
      var trigger = container.querySelector('[data-datasheet-trigger]');
      if (!trigger) return;

      var dropdown = container.querySelector('[data-datasheet-dropdown]');

      if (!dropdown) {
        trigger.addEventListener('click', function() {
          var datasheetUrl = container.getAttribute('data-datasheet-url');
          if (datasheetUrl) window.open(datasheetUrl, '_blank');
        });
        return;
      }

      var choiceView = dropdown.querySelector('[data-choice-view]');
      var formView = dropdown.querySelector('[data-form-view]');
      var showFormBtn = dropdown.querySelector('[data-show-form]');
      var skipBtn = dropdown.querySelector('[data-skip-form]');
      var cancelBtn = dropdown.querySelector('[data-cancel-form]');
      var projectForm = dropdown.querySelector('[data-project-form]');

      trigger.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        var isExpanded = trigger.getAttribute('aria-expanded') === 'true';
        if (isExpanded) {
          theme.datasheet.closeDropdown(trigger, dropdown, choiceView, formView);
        } else {
          // Close all other open dropdowns first
          theme.datasheet.closeAllDropdowns();
          theme.datasheet.openDropdown(trigger, dropdown);
        }
      });

      if (showFormBtn && formView && choiceView) {
        showFormBtn.addEventListener('click', function() {
          choiceView.setAttribute('data-hidden', 'true');
          formView.setAttribute('data-visible', 'true');
          var firstInput = formView.querySelector('input');
          if (firstInput) setTimeout(function() { firstInput.focus(); }, 100);
        });
      }

      if (skipBtn) {
        skipBtn.addEventListener('click', function() {
          var url = skipBtn.getAttribute('data-datasheet-url');
          window.open(url, '_blank');
          theme.datasheet.closeDropdown(trigger, dropdown, choiceView, formView);
        });
      }

      if (cancelBtn && formView && choiceView) {
        cancelBtn.addEventListener('click', function() {
          formView.setAttribute('data-visible', 'false');
          choiceView.setAttribute('data-hidden', 'false');
          if (projectForm) projectForm.reset();
        });
      }

      if (projectForm) {
        projectForm.addEventListener('submit', function(e) {
          e.preventDefault();
          var formData = new FormData(projectForm);
          var baseUrl = skipBtn.getAttribute('data-datasheet-url');
          var params = new URLSearchParams();
          for (var pair of formData.entries()) {
            if (pair[1].trim()) params.append(pair[0], pair[1].trim());
          }
          var url = baseUrl + (params.toString() ? '&' + params.toString() : '');
          window.open(url, '_blank');
          projectForm.reset();
          theme.datasheet.closeDropdown(trigger, dropdown, choiceView, formView);
        });
      }
    },

    closeAllDropdowns: function() {
      var containers = document.querySelectorAll('[data-datasheet-container]');
      for (var i = 0; i < containers.length; i++) {
        var t = containers[i].querySelector('[data-datasheet-trigger]');
        var d = containers[i].querySelector('[data-datasheet-dropdown]');
        var cv = d ? d.querySelector('[data-choice-view]') : null;
        var fv = d ? d.querySelector('[data-form-view]') : null;
        if (t && d) this.closeDropdown(t, d, cv, fv);
      }
    },

    openDropdown: function(trigger, dropdown) {
      trigger.setAttribute('aria-expanded', 'true');
      dropdown.setAttribute('aria-hidden', 'false');
    },

    closeDropdown: function(trigger, dropdown, choiceView, formView) {
      trigger.setAttribute('aria-expanded', 'false');
      dropdown.setAttribute('aria-hidden', 'true');
      setTimeout(function() {
        if (choiceView) choiceView.setAttribute('data-hidden', 'false');
        if (formView) formView.setAttribute('data-visible', 'false');
      }, 300);
    },

    /* ========================================================================
       PROJECT INFO (from URL params)
       ======================================================================== */

    handleProjectInfo: function() {
      var urlParams = new URLSearchParams(window.location.search);
      var projectBox = document.getElementById('project-info-box');
      if (!projectBox) return;

      var typeElement = document.getElementById('project-type');
      var projectElement = document.getElementById('project-name');
      var architectElement = document.getElementById('project-architect');
      var notesElement = document.getElementById('project-notes');
      var hasAnyInfo = false;

      // Type Number
      var typeValue = urlParams.get('type');
      if (typeValue && typeElement) {
        typeElement.textContent = typeValue;
        hasAnyInfo = true;
      } else {
        var typeRow = document.querySelector('[data-project-field="type"]');
        if (typeRow) typeRow.style.display = 'none';
      }

      // Project Name
      var projectValue = urlParams.get('project');
      if (projectValue && projectElement) {
        projectElement.textContent = projectValue;
        hasAnyInfo = true;
      } else {
        var projectRow = document.querySelector('[data-project-field="project"]');
        if (projectRow) projectRow.style.display = 'none';
      }

      // Architect / Designer
      var architectValue = urlParams.get('architect');
      if (architectValue && architectElement) {
        architectElement.textContent = architectValue;
        hasAnyInfo = true;
      } else {
        var architectRow = document.querySelector('[data-project-field="architect"]');
        if (architectRow) architectRow.style.display = 'none';
      }

      // Notes
      var notesValue = urlParams.get('notes');
      if (notesValue && notesElement) {
        notesElement.textContent = notesValue;
        hasAnyInfo = true;
      } else {
        var notesRow = document.querySelector('[data-project-field="notes"]');
        if (notesRow) notesRow.style.display = 'none';
      }

      if (hasAnyInfo) {
        projectBox.style.display = 'table';
      }
    },

    /* ========================================================================
       TIMESTAMP & PRINT
       ======================================================================== */

    generateTimestamp: function() {
      var el = document.getElementById('generated-timestamp');
      if (!el) return;

      var now = new Date();
      var day = String(now.getDate()).padStart(2, '0');
      var month = String(now.getMonth() + 1).padStart(2, '0');
      var year = now.getFullYear();
      var hours = now.getHours();
      var minutes = String(now.getMinutes()).padStart(2, '0');
      var ampm = hours >= 12 ? 'pm' : 'am';
      hours = hours % 12 || 12;

      el.textContent = 'Generated ' + day + '/' + month + '/' + year + ' @ ' + hours + ':' + minutes + ampm;
    },

    /* ========================================================================
       PDF FILENAME
       Builds "{Project Name} - {Type Number} - {SKU} - {Product Name} -
       Versalux Lighting Systems" from the project-info URL params plus the
       server-exposed SKU / product name, dropping any empty parts. This is the
       suggested filename Chrome uses for the "Save as PDF" destination.
       ======================================================================== */

    buildPdfFilename: function() {
      var params = new URLSearchParams(window.location.search);
      var project = (params.get('project') || '').trim();
      var type = (params.get('type') || '').trim();
      var sku = (window.__datasheetSku || '').trim();
      var name = (window.__datasheetProductName || '').trim();

      var parts = [project, type, sku, name].filter(Boolean); // drop empty parts
      var full = (parts.length ? parts.join(' - ') + ' - ' : '') + 'Versalux Lighting Systems';

      // Sanitize so Windows-Chrome "Save as PDF" never goes blank: dots make
      // Chrome treat the title as having a file extension, and the rest are
      // illegal Windows filename characters.
      return full
        .replace(/\./g, '-')
        .replace(/[<>:"/\\|?*]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    },

    setPdfTitle: function() {
      var title = this.buildPdfFilename();
      if (title) {
        document.title = title;
        window.__datasheetPdfTitle = title;
      }
    },

    setupManualPrint: function() {
      var printBtn = document.getElementById('manual-print-btn');
      if (!printBtn) return;

      printBtn.addEventListener('click', function() {
        // Re-assert the print/PDF filename right before print so Chrome's
        // Save-as-PDF dialog reliably picks it up as the suggested filename.
        theme.datasheet.setPdfTitle();
        document.body.offsetHeight;
        requestAnimationFrame(function() {
          setTimeout(function() {
            window.print();
          }, 300);
        });
      });
    }
  };

  // Initialize on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      theme.datasheet.init();
    });
  } else {
    theme.datasheet.init();
  }

  document.addEventListener('page:loaded', function() {
    theme.datasheet.init();
  });

})();
